import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';

// HOT's backorder report (hot_back_order, reloaded nightly 11:30pm by
// 15_hot_get_backorder_data.py) enriched by get_hot_back_orders() with our
// Cin7 code, Cin7 PO and the open customer orders waiting on each item.

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—');

// products.name carries HTML entities from Cin7 (e.g. "&amp;").
const decode = (s) => {
  if (!s) return s;
  const el = document.createElement('textarea');
  el.innerHTML = s;
  return el.value;
};

const FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'customers', label: 'Customer waiting' },
  { key: 'unmatched', label: 'No Cin7 code' },
];

const HotBackOrders = () => {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [filter, setFilter]   = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error: rpcError } = await supabase.rpc('get_hot_back_orders');
    if (rpcError) {
      setError(rpcError.message);
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === 'customers' && !r.customer_orders?.length) return false;
      if (filter === 'unmatched' && r.cin7_code) return false;
      if (!term) return true;
      const hay = [
        r.item_code, r.cin7_code, r.product_name, r.po_number, r.ref_no, r.cin7_po,
        String(r.hot_order),
        ...(r.customer_orders || []).flatMap((c) => [c.order_ref, c.customer]),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    });
  }, [rows, search, filter]);

  const runDate = rows[0]?.run_date;

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/utilities')}>← Utilities</button>
        <span className="page-nav__title">HOT Back Orders</span>
      </div>

      <section className="section">
        <SectionHeader title="HOT Back Orders" count={visible.length} onRefresh={load} loading={loading} />
        <p className="section__desc">
          Items HOT has on backorder for us, from their backorder report
          {runDate ? ` (loaded ${fmtDate(runDate)})` : ''}. Refreshed nightly. Customer orders are open
          sales orders with unmoved qty on that Cin7 code.
        </p>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '0 16px 12px' }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code, product, HOT order, PO, customer order..."
            style={{ padding: '6px 10px', fontSize: 12, minWidth: 320 }}
          />
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`action-btn ${filter === f.key ? '' : 'action-btn--ghost'}`}
              style={{ fontSize: 11, padding: '4px 12px' }}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {error && <div className="empty-row" style={{ color: '#c00' }}>{error}</div>}
        {loading && <div className="loading-row">Loading...</div>}
        {!loading && !error && !visible.length && <div className="empty-row">No backorder lines match.</div>}

        {!loading && visible.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Cin7 Code</th>
                  <th>HOT Code</th>
                  <th>Product</th>
                  <th className="num">Qty</th>
                  <th>HOT Order</th>
                  <th>Ordered</th>
                  <th className="num">Days</th>
                  <th>Cin7 PO</th>
                  <th>Customer Orders</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r, i) => (
                  <tr key={`${r.hot_order}-${r.item_code}-${i}`} className="data-row">
                    <td>
                      {r.cin7_code
                        ? <strong>{r.cin7_code}</strong>
                        : <span style={{ color: '#B91C1C', fontSize: 11 }}>not found</span>}
                    </td>
                    <td>{r.item_code}</td>
                    <td style={{ maxWidth: 320 }}>{decode(r.product_name) || '—'}</td>
                    <td className="num">{Number(r.qty)}</td>
                    <td>
                      {r.po_number}
                      {r.ref_no && <div style={{ fontSize: 11, color: '#666' }}>Ref {r.ref_no}</div>}
                    </td>
                    <td>{fmtDate(r.order_date)}</td>
                    <td className="num" style={{ fontWeight: r.days_waiting > 60 ? 700 : 400, color: r.days_waiting > 60 ? '#B91C1C' : undefined }}>
                      {r.days_waiting}
                    </td>
                    <td>{r.cin7_po || '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      {r.customer_orders?.length
                        ? r.customer_orders.map((c) => (
                          <div key={c.order_ref}>
                            <strong>{c.order_ref}</strong>
                            <span style={{ color: '#666' }}> · {c.customer || '—'} · {c.stage} · qty {Number(c.qty)}</span>
                          </div>
                        ))
                        : <span style={{ color: '#999' }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default HotBackOrders;
